<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { RouterView } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import AuthFooter from './AuthFooter.vue'
import { useIsKycSdkFullScreen } from '@modules/onboarding'
import { useScrollTopOnRouteChange } from '@shared/composables/useScrollTopOnRouteChange'

// Onboarding page chrome: full-bleed background + the legal footer strip.
// The top bar, progress line and the two-column main (step card + right rail)
// belong to the wizard container — they are driven by the step machine, and
// keeping them there means the layout never has to know which step is active.
//
// `isSdkFullScreen` is the one wrinkle: the Sumsub SDK takes over the entire
// viewport and the footer must step out of the way. The layout reads this
// single flag from the onboarding module's public surface.

const isSdkFullScreen = useIsKycSdkFullScreen()
const shellRef = useTemplateRef<InstanceType<typeof Box>>('shell')
useScrollTopOnRouteChange(() => shellRef.value?.$el)
</script>

<template>
  <!-- Single RouterView — never remounts when isSdkFullScreen toggles.
       Splitting into two v-if/v-else branches with RouterView in each caused
       OnboardingWizardContainer to remount and reset identitySubStep to 'form'. -->
  <Box
    ref="shell"
    is="main"
    :class="['onboarding-shell', { 'onboarding-shell--sdk': isSdkFullScreen }]"
    direction="col"
    block
  >
    <Box class="onboarding-shell__body" direction="col" grow>
      <RouterView />
    </Box>

    <AuthFooter v-if="!isSdkFullScreen" class="onboarding-shell__footer" />
  </Box>
</template>

<style scoped>
/* THE scroll container for onboarding. `html/body/#app` are `overflow: hidden`
   app-wide, so without this the wizard / Sumsub SDK form is clipped at the
   viewport edge on shorter screens and mobile. */
.onboarding-shell {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--p-surface-50);
  box-sizing: border-box;
}

/* Never shrink below content — the shell scrolls instead, and the footer
   stays below the body rather than overlapping it. */
.onboarding-shell__body {
  flex-shrink: 0;
  min-width: 0;
  width: 100%;
}

.onboarding-shell__footer {
  border-top: none;
}

.onboarding-shell--sdk {
  background: var(--p-surface-50);
}
</style>
